import type { TimeUnit, TimeValue } from "@/shared/ui/dropdown"

/** ISO 8601 durations only encode finite time spans — "forever" is not representable on the wire. */
export type FiniteTimeUnit = Exclude<TimeUnit, "forever">

interface IsoDurationParts {
  value: number
  unit: FiniteTimeUnit
}

const FINITE_TIME_UNIT_SECONDS: Record<FiniteTimeUnit, number> = {
  m: 60,
  h: 3_600,
  d: 86_400,
  w: 604_800,
  y: 31_536_000
}

const ISO_DURATION_PREFIX: Record<FiniteTimeUnit, "P" | "PT"> = {
  m: "PT",
  h: "PT",
  d: "P",
  w: "P",
  y: "P"
}

const ISO_DURATION_SUFFIX: Record<FiniteTimeUnit, "M" | "H" | "D" | "W" | "Y"> = {
  m: "M",
  h: "H",
  d: "D",
  w: "W",
  y: "Y"
}

const ISO_DURATION_REGEX = /^P(?:(\d+)Y)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/

/**
 * Encodes a finite duration as an ISO 8601 string. Callers must map "forever"
 * to `null` (or a safe finite default) at their own domain boundary before
 * invoking this — the parameter type intentionally excludes `"forever"` so any
 * misuse surfaces as a compile error at the call site rather than a runtime
 * crash reaching the user. Consumers that hold a widened `TimeValue` and
 * cannot narrow structurally should use {@link toIsoDurationOrDefault}.
 */
export const toIsoDuration = (value: number, unit: FiniteTimeUnit): string =>
  `${ISO_DURATION_PREFIX[unit]}${value}${ISO_DURATION_SUFFIX[unit]}`

/**
 * Widened-TimeValue variant for call sites where TypeScript cannot prove the
 * unit is finite but the domain / UI gating already guarantees it. `"forever"`
 * degrades to `fallback` (default `"P1D"`) instead of a compile error at the
 * call site or a runtime crash at the API boundary. The fallback path is
 * expected to be unreachable in a correctly gated flow — it exists only so a
 * UI-layer regression degrades to a sensible payload rather than a broken save.
 */
export const toIsoDurationOrDefault = (value: number, unit: TimeUnit, fallback: string = "P1D"): string =>
  unit === "forever" ? fallback : toIsoDuration(value, unit)

/**
 * Decode-or-default variant of {@link fromIsoDuration}. The shared codebase
 * had accumulated ~8 file-local copies of `fromIsoDuration(iso) ?? DEFAULT`;
 * this hoists the pattern to a single helper so every consumer picks its own
 * fallback shape without duplicating the null-coalescing plumbing.
 */
export const fromIsoDurationOrDefault = (input: string, fallback: IsoDurationParts): IsoDurationParts =>
  fromIsoDuration(input) ?? fallback

export const fromIsoDuration = (input: string): IsoDurationParts | null => {
  const match = ISO_DURATION_REGEX.exec(input)
  if (!match) return null
  const [, years, weeks, days, hours, minutes] = match as unknown as [
    string,
    string | undefined,
    string | undefined,
    string | undefined,
    string | undefined,
    string | undefined
  ]
  if ([years, weeks, days, hours, minutes].every(part => part === undefined)) return null
  if (input.includes("T") && hours === undefined && minutes === undefined) return null

  // Years are calendar-relative. Combining them with fixed units would require
  // inventing a calendar convention, so only canonical year durations decode.
  if (years !== undefined) {
    if ([weeks, days, hours, minutes].some(part => part !== undefined)) return null
    return { value: Number(years), unit: "y" }
  }

  const weekCount = Number(weeks ?? 0)
  const dayCount = Number(days ?? 0)
  const hourCount = Number(hours ?? 0)
  const minuteCount = Number(minutes ?? 0)

  if (minutes !== undefined) {
    return { value: (weekCount * 7 + dayCount) * 1440 + hourCount * 60 + minuteCount, unit: "m" }
  }
  if (hours !== undefined) {
    return { value: (weekCount * 7 + dayCount) * 24 + hourCount, unit: "h" }
  }
  if (days !== undefined) return { value: weekCount * 7 + dayCount, unit: "d" }
  if (weeks !== undefined) return { value: weekCount, unit: "w" }
  return null
}

const ISO_PT_DURATION_REGEX = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/

/** Parses time-only ISO 8601 durations (`PT[nH][nM][nS]`). Returns null for date parts (P1D, P1W) or malformed input. */
export const isoDurationToSeconds = (input: string): number | null => {
  const match = ISO_PT_DURATION_REGEX.exec(input)
  if (!match) return null
  const [, hours, minutes, seconds] = match
  if (!hours && !minutes && !seconds) return null
  let total = 0
  if (hours) total += Number(hours) * 3600
  if (minutes) total += Number(minutes) * 60
  if (seconds) total += Number(seconds)
  return total
}

// Always emits PT{n}S; Pydantic normalizes back to canonical form (e.g. PT300S → PT5M) on the next read.
export const secondsToIsoDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    throw new TypeError(`secondsToIsoDuration: expected non-negative finite number, got ${seconds}`)
  }
  return `PT${Math.trunc(seconds)}S`
}

export const secondsToTimeValue = (
  seconds: number,
  allowedUnits: ReadonlyArray<FiniteTimeUnit> = ["y", "w", "d", "h", "m"]
): IsoDurationParts => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    throw new TypeError(`secondsToTimeValue: expected non-negative finite number, got ${seconds}`)
  }

  const wholeSeconds = Math.trunc(seconds)
  const orderedUnits = [...allowedUnits].sort(
    (left, right) => FINITE_TIME_UNIT_SECONDS[right] - FINITE_TIME_UNIT_SECONDS[left]
  )
  const fallbackUnit = orderedUnits.at(-1)
  if (!fallbackUnit) {
    throw new RangeError("secondsToTimeValue: allowedUnits must not be empty")
  }
  if (wholeSeconds === 0) return { value: 1, unit: fallbackUnit }

  for (const unit of orderedUnits) {
    const unitSeconds = FINITE_TIME_UNIT_SECONDS[unit]
    if (wholeSeconds % unitSeconds === 0) {
      return { value: wholeSeconds / unitSeconds, unit }
    }
  }

  return {
    value: Math.max(1, Math.round(wholeSeconds / FINITE_TIME_UNIT_SECONDS[fallbackUnit])),
    unit: fallbackUnit
  }
}

export const timeValueToSeconds = (timeValue: TimeValue): number | null => {
  if (timeValue.unit === "forever") return null
  const unitSeconds = FINITE_TIME_UNIT_SECONDS[timeValue.unit]
  return Math.max(1, Math.round(timeValue.value)) * unitSeconds
}
